import { useMemo, useState } from 'react'
import type { NtBlock, NtSchedule } from '../../core/types'
import { useNorseTerm } from './NorseTermData'
import { setArtifactBlock } from './model'
import { ArtifactList } from './ArtifactOptions'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Artifacts — every student makes one, in their morning or their afternoon
 * course. Students choose; the office sees who has not, sets it for them, and
 * can change anyone's. Per course, who is making their artifact there — which
 * is also who that teacher has on Friday morning.
 */
export function ArtifactsOffice({ who }: { who: string }) {
  const { schedules, courses } = useNorseTerm()
  const [q, setQ] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  // Credit recovery all day makes no artifact: nothing to choose or chase.
  const coursed = schedules.filter((s) => !s.independent && !s.noArtifact)
  const unchosen = coursed.filter((s) => !s.artifactBlock).sort((a, b) => a.name.localeCompare(b.name))
  const independent = schedules.filter((s) => s.independent).length

  const set = (s: NtSchedule, b: NtBlock) => async () => {
    setBusy(s.id); setErr(null)
    try { await setArtifactBlock(s.id, b, who) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(null) }
  }

  const byCourse = useMemo(() => (courses ?? [])
    .filter((c) => c.status === 'approved' && c.block)
    .sort((a, b) => (a.block === b.block ? a.title.localeCompare(b.title) : a.block === 'AM' ? -1 : 1))
    .map((c) => ({
      c, makers: coursed.filter((s) => s.artifactBlock === c.block && (c.block === 'AM' ? s.amCourseId : s.pmCourseId) === c.id),
    })), [courses, coursed])

  const found = q.trim().length < 2 ? [] : coursed.filter((s) => s.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)

  if (!schedules.length) {
    return <Empty title="No schedules yet" body="Students choose their artifact course once placement has run." />
  }

  const Choices = ({ s }: { s: NtSchedule }) => (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {(['AM', 'PM'] as const).filter((b) => (b === 'AM' ? s.amCourseId && !s.amCreditRecovery : s.pmCourseId && !s.pmCreditRecovery)).map((b) => (
        <button key={b} className={`chip${s.artifactBlock === b ? ' is-on' : ''}`} disabled={busy === s.id} onClick={set(s, b)}>
          {b === 'AM' ? 'Morning' : 'Afternoon'}: {b === 'AM' ? s.amTitle : s.pmTitle}
        </button>
      ))}
    </span>
  )

  return (
    <div className="wide">
      <div className="card" style={{ marginTop: 'var(--s-2)' }}>
        <div className="dl">
          <div><div className="dl__k">Chosen</div><div className="dl__v">{coursed.length - unchosen.length} of {coursed.length}</div></div>
          <div><div className="dl__k">Not chosen</div><div className="dl__v">{unchosen.length}</div></div>
          <div><div className="dl__k">Independent projects</div><div className="dl__v">{independent}</div></div>
        </div>
        <div className="meter" aria-hidden="true"><span style={{ width: `${coursed.length ? ((coursed.length - unchosen.length) / coursed.length) * 100 : 0}%` }} /></div>
      </div>
      {err && <ErrorState body={`Not saved. ${err}`} />}

      {unchosen.length > 0 && (
        <>
          <h2 className="work__heading">Not chosen yet <span className="work__badge">{unchosen.length}</span></h2>
          <ul className="dash__people">
            {unchosen.map((s) => (
              <li key={s.id}><span style={{ fontWeight: 600 }}>{s.name}</span><span className="work__sub">Grade {s.grade}</span><Choices s={s} /></li>
            ))}
          </ul>
        </>
      )}

      <h2 className="work__heading">Change a student’s choice</h2>
      <input className="opt__input" placeholder="Find a student by name" aria-label="Find a student by name" value={q} onChange={(e) => setQ(e.target.value)} />
      {found.length > 0 && (
        <ul className="dash__people">
          {found.map((s) => (
            <li key={s.id}><span style={{ fontWeight: 600 }}>{s.name}</span>
              <span className="work__sub">{s.artifactBlock ? `set by ${s.artifactSetBy ?? 'someone'}` : 'not chosen'}</span><Choices s={s} /></li>
          ))}
        </ul>
      )}

      <h2 className="work__heading">By course: who is making their artifact there (and is there Friday morning)</h2>
      {byCourse.map(({ c, makers }) => (
        <section key={c.id} className="card" style={{ marginTop: 'var(--s-2)' }}>
          <div className="pagehead">
            <div>
              <div style={{ fontWeight: 620 }}>{c.block === 'AM' ? 'Morning' : 'Afternoon'} · {c.title}</div>
              <div className="work__sub">{c.teacherName} · {c.room || 'no room'}</div>
            </div>
            <span className="pill">{makers.length} making an artifact</span>
          </div>
          <ArtifactList options={c.artifactOptions} compact />
          {makers.length > 0 && <p className="work__sub" style={{ marginTop: 'var(--s-2)' }}>{makers.map((m) => m.name).join(', ')}</p>}
        </section>
      ))}
    </div>
  )
}
